import React, { useState } from 'react';
import { Container, Spinner, Alert } from 'react-bootstrap';
import DesktopLayout from '../layouts/DesktopLayout';
import ScoreCard from '../components/opsDashboard/ScoreCard';
import MonthlyKpiPanel from '../components/opsDashboard/MonthlyKpiPanel';
import useLanguage from '../hooks/useLanguage';
import { useMonthlyKpis, useSupportingReviews, useMessageResponse } from '../hooks/useOpsPerformance';
import { formatDate } from '../utils/dateFormatter';
import { fill } from '../components/opsDashboard/kpiText';
import '../styles/OpsPerformance.css';

/**
 * «مؤشر أداء العمليات» — monthly monitoring of the ops manager's two KPIs (guest rating, occupancy), then
 * the supporting figures (tasks/ops-performance-kpi-restructure.md). No target or colour for now.
 * Per-unit figures, reasons and verdicts live on «أداء الوحدات».
 * Internal-only (admin / ops / ops-manager). Reads our own DB via /api/ops/dashboard/*.
 */

const NO_READING = '—';

const SupportingFigures = () => {
  const { t, language } = useLanguage();
  const reviews = useSupportingReviews();
  const replies = useMessageResponse();
  const rv = reviews.data;
  const msg = replies.data;
  const share = rv?.conversion;
  const settled = share?.checkoutPeriod;
  const day = (date) => formatDate(date, {}, language);
  const withSample = (text, lowSample) => (lowSample ? `${text} ${t('opsPerformance.support.lowSample')}` : text);
  const waiting = (query) => t(query.isError ? 'opsPerformance.support.failed' : 'opsPerformance.support.loading');

  let shareNote = waiting(reviews);
  if (share) {
    shareNote = share.eligibleCheckouts > 0
      ? withSample(fill(t('opsPerformance.support.shareNote'), { reviewed: share.reviewedCheckouts, eligible: share.eligibleCheckouts }), share.lowSample)
      : t('opsPerformance.support.noCheckouts');
  }

  return (
    <div className="ops-support-sheet">
      <ScoreCard
        value={msg?.pctWithinTarget != null ? <bdi dir="ltr">{`${msg.pctWithinTarget}%`}</bdi> : NO_READING}
        label={t('opsPerformance.support.replyLabel')}
        info={t('opsPerformance.support.replyInfo')}
        sublabel={msg
          ? withSample(fill(t('opsPerformance.support.pending'), { count: msg.pending ?? 0 }), msg.pctWithinTarget != null && msg.lowSample)
          : waiting(replies)}
      />
      <ScoreCard
        value={rv ? rv.lowRatingCount : NO_READING}
        label={t('opsPerformance.support.lowLabel')}
        info={t('opsPerformance.support.lowInfo')}
        sublabel={rv ? t('opsPerformance.support.lastWeek') : waiting(reviews)}
      />
      <ScoreCard
        value={share?.pct != null ? <bdi dir="ltr">{`${share.pct}%`}</bdi> : NO_READING}
        label={t('opsPerformance.support.shareLabel')}
        info={settled
          ? fill(t('opsPerformance.support.shareInfo'), { from: day(settled.startDate), to: day(settled.endDate) })
          : t('opsPerformance.support.shareLabel')}
        sublabel={shareNote}
      />
    </div>
  );
};

const OperationsPerformanceDashboard = () => {
  const [activeNavItem, setActiveNavItem] = useState('ops-performance');
  const { t, isArabic } = useLanguage();
  const kpis = useMonthlyKpis();

  return (
    <DesktopLayout
      activeNavItem={activeNavItem}
      setActiveNavItem={setActiveNavItem}
      headerTitle={t('opsPerformance.title')}
    >
      <Container fluid className="p-2 p-md-4 ops-performance" dir={isArabic ? 'rtl' : 'ltr'}>
        <h2 className="text-dark fw-bold mb-2">{t('opsPerformance.title')}</h2>
        <p className="text-muted mb-4">{t('opsPerformance.subtitle')}</p>

        {kpis.isLoading && <div className="text-center py-5"><Spinner animation="border" variant="secondary" /></div>}
        {kpis.isError && <Alert variant="light" className="border text-center mb-0">{t('opsPerformance.loadFailed')}</Alert>}
        {kpis.data && <MonthlyKpiPanel data={kpis.data} />}

        <div className="ops-section-head">
          <h3 className="ops-section-title">{t('opsPerformance.support.title')}</h3>
        </div>
        <SupportingFigures />
      </Container>
    </DesktopLayout>
  );
};

export default OperationsPerformanceDashboard;
